import Alert from '@/components/elements/notifications/Alert/Alert';
import Icon from '@/components/icons/Icon';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { useRef } from 'react';
import { useUserInfoBanner, useUserInfoBannerOffset } from './UserInfoBanner.hooks';
import { ActiveUserInfoBanner, UserInfoBannerSeverity } from './UserInfoBanner.types';

const SEVERITY_CONFIG: {
  [key in UserInfoBannerSeverity]: {
    alertVariant: 'warning' | 'cation' | 'information' | 'success';
    icon: 'error' | 'warning' | 'info-circle' | null;
    iconColor: 'black-900' | 'primary-900';
    textColor: 'text-black-900' | 'text-primary-900';
  };
} = {
  error: { alertVariant: 'warning', icon: 'error', iconColor: 'black-900', textColor: 'text-black-900' },
  warning: { alertVariant: 'cation', icon: 'warning', iconColor: 'primary-900', textColor: 'text-primary-900' },
  info: {
    alertVariant: 'information',
    icon: 'info-circle',
    iconColor: 'primary-900',
    textColor: 'text-primary-900',
  },
  positive: { alertVariant: 'success', icon: null, iconColor: 'primary-900', textColor: 'text-primary-900' },
};

const UserInfoBannerAlert = ({ banner, dismiss }: { banner: ActiveUserInfoBanner; dismiss: () => void }) => {
  const { isMobileView } = useMobileView();
  const { alertVariant, icon, iconColor, textColor } = SEVERITY_CONFIG[banner.severity];
  const message = _t(`features.user-info-banner.UserInfoBanner.${banner.message}`);
  const iconElement = icon && <Icon variant={icon} size="sm" color={iconColor} className="shrink-0" />;
  // Fills an empty side slot so the message stays centred on the whole banner.
  const spacer = <span aria-hidden="true" className="block" />;
  const closeButton = banner.dismissable && (
    <button type="button" aria-label={_t('close')} onClick={dismiss} className="flex items-center">
      <Icon variant="close" size="sm" color={iconColor} />
    </button>
  );

  // Mobile drops the severity icon; desktop centres icon and message together.
  const slots = isMobileView
    ? {
        leftSlot: closeButton ? spacer : undefined,
        middleSlot: <p className="text-m flex-1 text-center">{message}</p>,
        rightSlot: closeButton || undefined,
      }
    : {
        leftSlot: closeButton ? spacer : undefined,
        middleSlot: (
          <div className="gap-sm flex flex-1 items-center justify-center">
            {iconElement}
            <p className="text-m text-center">{message}</p>
          </div>
        ),
        rightSlot: closeButton || undefined,
      };

  return <Alert variant={alertVariant} className={`rounded-none ${textColor}`} {...slots} />;
};

/**
 * Top-of-page user info banner, controlled by the Confidence flag
 * `user-info-banner`. Rendered in the app shell on all pages; hidden whenever
 * the flag is off, unresolved, or carries an unknown message.
 */
const UserInfoBanner = () => {
  const { banner, dismiss } = useUserInfoBanner();
  const bannerRef = useRef<HTMLDivElement>(null);
  useUserInfoBannerOffset(bannerRef, banner !== null);

  // Stays mounted while hidden: screen readers only reliably announce content
  // that changes inside a live region that already exists.
  return (
    <div role="status" ref={bannerRef}>
      {banner && <UserInfoBannerAlert banner={banner} dismiss={dismiss} />}
    </div>
  );
};

export default UserInfoBanner;
